import { useCallback } from 'react';
import { captureAppMessage } from '@leagueplatform/observability';
import { SENTRY_CONSTANTS } from 'common/constants';

interface UseErrorHandlerReturn {
  handleProfileError: (error: Error) => void;
  handleCommunicationError: (error: Error) => void;
  handleAccountSetupError: (error: Error) => void;
  handleGenericError: (
    error: Error,
    context: string,
    customContext?: any,
  ) => void;
}

export const useErrorHandler = (): UseErrorHandlerReturn => {
  const handleProfileError = useCallback((error: Error) => {
    captureAppMessage(`Failed to update profile settings - ${error.name}`, {
      context: {
        contextDetails: {
          info: `An exception occurred during profile update. Error: ${error.message}`,
        },
      },
      ownershipTeam: SENTRY_CONSTANTS.OWNERSHIP_TEAM,
      severityLevel: 'error',
    });
  }, []);

  const handleCommunicationError = useCallback((error: Error) => {
    captureAppMessage(
      `Failed to update communication preferences - ${error.name}`,
      {
        context: {
          contextDetails: {
            info: `An exception occurred during communication update. Error: ${error.message}`,
          },
        },
        ownershipTeam: SENTRY_CONSTANTS.OWNERSHIP_TEAM,
        severityLevel: 'error',
      },
    );
  }, []);

  const handleAccountSetupError = useCallback((error: Error) => {
    captureAppMessage(`Failed to complete account setup - ${error.name}`, {
      context: {
        contextDetails: {
          info: `An exception occurred during account setup. Error: ${error.message}`,
        },
      },
      ownershipTeam: SENTRY_CONSTANTS.OWNERSHIP_TEAM,
      severityLevel: 'error',
    });
  }, []);

  const handleGenericError = useCallback(
    (error: Error, context: string, customContext?: any) => {
      captureAppMessage(`Failed to ${context} - ${error.name}`, {
        context: {
          contextDetails: {
            info: `An exception occurred during ${context}. Error: ${error.message}`,
            ...customContext,
          },
        },
        ownershipTeam: SENTRY_CONSTANTS.OWNERSHIP_TEAM,
        severityLevel: 'error',
      });
    },
    [],
  );

  return {
    handleProfileError,
    handleCommunicationError,
    handleAccountSetupError,
    handleGenericError,
  };
};
